import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  fetchCourseAssessments, submitCourseAssessment,
  fetchLessonAssessments, submitLessonAssessment,
  fetchCurriculum,
} from "./adminCourseService";
import { fetchActiveSpeakers } from "../services/adminSpeakerServices";
import "./adminCourses.css";

const CRITERIA = [
  { key: "scoreFluency",     label: "Fluency"     },
  { key: "scoreClarity",     label: "Clarity"     },
  { key: "scoreVocabulary",  label: "Vocabulary"  },
  { key: "scoreGrammar",     label: "Grammar"     },
  { key: "scoreInteraction", label: "Interaction" },
];

const BLANK = {
  scoreFluency: 3, scoreClarity: 3, scoreVocabulary: 3,
  scoreGrammar: 3, scoreInteraction: 3,
  instructorFeedback: "", improvementGoals: "", assessedBySpeakerId: "",
};

const liveTotal = (s) => CRITERIA.reduce((sum, c) => sum + Number(s[c.key] || 0), 0);

const fmtDate = (iso) => iso
  ? new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
  : "";

/* ── Score bar ── */
const ScoreBar = ({ label, value, compareValue }) => {
  const improved = compareValue != null && value > compareValue;
  const declined = compareValue != null && value < compareValue;
  return (
    <div style={{ marginBottom: "0.65rem" }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.2rem" }}>
        <span style={{ fontSize: "0.83rem", color: "#374151" }}>{label}</span>
        <span style={{ fontSize: "0.83rem", fontWeight: 700, color: improved ? "#16a34a" : declined ? "#dc2626" : "#374151" }}>
          {value}/5{improved ? " ↑" : declined ? " ↓" : ""}
        </span>
      </div>
      <div style={{ height: 7, background: "#e5e7eb", borderRadius: 4, position: "relative" }}>
        {compareValue != null && (
          <div style={{ position: "absolute", left: 0, top: 0, width: `${(compareValue / 5) * 100}%`, height: "100%", background: "#bfdbfe", borderRadius: 4 }} />
        )}
        <div style={{ position: "absolute", left: 0, top: 0, width: `${(value / 5) * 100}%`, height: "100%", background: improved ? "#16a34a" : declined ? "#dc2626" : "#2563eb", borderRadius: 4, opacity: 0.85 }} />
      </div>
    </div>
  );
};

/* ── Read-only assessment card ── */
const AssessCard = ({ data, type, compareData, onEdit }) => {
  const label    = type === "INITIAL" ? "Initial Assessment" : "Final Assessment";
  const badgeCls = type === "INITIAL" ? "ac-badge-blue" : "ac-badge-green";

  if (!data) return (
    <div style={{ background: "#f9fafb", border: "1px dashed #d1d5db", borderRadius: 10, padding: "1.5rem", textAlign: "center", color: "#9ca3af", fontSize: "0.88rem", flex: 1, minWidth: 240 }}>
      <div style={{ fontSize: "1.5rem", marginBottom: "0.5rem" }}>📋</div>
      {label} not yet submitted.
      {onEdit && <div style={{ marginTop: "0.75rem" }}><button className="ac-btn ac-btn-primary ac-btn-sm" onClick={onEdit}>+ Add</button></div>}
    </div>
  );

  return (
    <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, padding: "1.25rem", flex: 1, minWidth: 240, boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
        <span className={`ac-badge ${badgeCls}`}>{label}</span>
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <span style={{ fontSize: "1.3rem", fontWeight: 700, color: "#1f2937" }}>
            {data.totalScore}<span style={{ fontSize: "0.8rem", color: "#9ca3af", fontWeight: 400 }}>/25</span>
          </span>
          {onEdit && <button className="ac-btn ac-btn-ghost ac-btn-sm" onClick={onEdit}>Edit</button>}
        </div>
      </div>
      {CRITERIA.map((c) => (
        <ScoreBar key={c.key} label={c.label} value={data[c.key]} compareValue={compareData?.[c.key] ?? null} />
      ))}
      {data.instructorFeedback && (
        <div style={{ marginTop: "0.75rem" }}>
          <div style={{ fontSize: "0.72rem", fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "0.2rem" }}>Feedback</div>
          <p style={{ fontSize: "0.85rem", color: "#374151", margin: 0, lineHeight: 1.5 }}>{data.instructorFeedback}</p>
        </div>
      )}
      {data.improvementGoals && (
        <div style={{ marginTop: "0.5rem" }}>
          <div style={{ fontSize: "0.72rem", fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "0.2rem" }}>Goals</div>
          <p style={{ fontSize: "0.85rem", color: "#374151", margin: 0, lineHeight: 1.5 }}>{data.improvementGoals}</p>
        </div>
      )}
      <div style={{ marginTop: "0.75rem", fontSize: "0.75rem", color: "#9ca3af" }}>
        By {data.assessedBySpeakerName || "—"} · {fmtDate(data.assessedAt)}
      </div>
    </div>
  );
};

/* ── Score slider ── */
const ScoreSlider = ({ label, value, onChange }) => (
  <div style={{ marginBottom: "1rem" }}>
    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.25rem" }}>
      <label style={{ fontSize: "0.88rem", fontWeight: 600, color: "#374151" }}>{label}</label>
      <span style={{ fontSize: "0.88rem", fontWeight: 700, color: value >= 4 ? "#16a34a" : value <= 2 ? "#dc2626" : "#d97706" }}>
        {value}/5
      </span>
    </div>
    <input type="range" min={1} max={5} step={1} value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      style={{ width: "100%", accentColor: "#2563eb" }}
    />
    <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.7rem", color: "#9ca3af" }}>
      <span>1 — Poor</span><span>3 — Average</span><span>5 — Excellent</span>
    </div>
  </div>
);

/* ── Assessment form (shared, scope-aware) ── */
const AssessForm = ({ type, scope, curriculumId, lessonLabel, existingData, speakers, onSave, onCancel, saving, error }) => {
  const [scores, setScores] = useState(existingData ? {
    scoreFluency:        existingData.scoreFluency,
    scoreClarity:        existingData.scoreClarity,
    scoreVocabulary:     existingData.scoreVocabulary,
    scoreGrammar:        existingData.scoreGrammar,
    scoreInteraction:    existingData.scoreInteraction,
    instructorFeedback:  existingData.instructorFeedback || "",
    improvementGoals:    existingData.improvementGoals || "",
    assessedBySpeakerId: existingData.assessedBySpeakerId || "",
  } : { ...BLANK });

  const set = (k, v) => setScores((p) => ({ ...p, [k]: v }));

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({ ...scores, assessmentType: type, scope, curriculumId });
  };

  return (
    <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, padding: "1.5rem", boxShadow: "0 1px 4px rgba(0,0,0,0.06)", maxWidth: 560 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.25rem" }}>
        <div>
          <h3 style={{ margin: 0, fontSize: "1rem", fontWeight: 700 }}>
            {type === "INITIAL" ? "Initial" : "Final"} Assessment
            <span style={{ marginLeft: 8, fontSize: "0.75rem", fontWeight: 600, color: scope === "COURSE" ? "#2563eb" : "#7c3aed", background: scope === "COURSE" ? "#dbeafe" : "#ede9fe", padding: "2px 8px", borderRadius: 999 }}>
              {scope === "COURSE" ? "Course" : "Lesson"}
            </span>
          </h3>
          {lessonLabel && <div style={{ fontSize: "0.78rem", color: "#6b7280", marginTop: 3 }}>{lessonLabel}</div>}
        </div>
        <div style={{ background: "#eff6ff", borderRadius: 8, padding: "0.3rem 0.75rem", textAlign: "center" }}>
          <div style={{ fontSize: "0.68rem", color: "#6b7280" }}>Total</div>
          <div style={{ fontSize: "1.3rem", fontWeight: 700, color: "#2563eb", lineHeight: 1 }}>
            {liveTotal(scores)}<span style={{ fontSize: "0.72rem", color: "#9ca3af" }}>/25</span>
          </div>
        </div>
      </div>

      {error && <div style={{ background: "#fee2e2", color: "#b91c1c", padding: "0.5rem 0.75rem", borderRadius: 6, marginBottom: "1rem", fontSize: "0.85rem" }}>{error}</div>}

      <form onSubmit={handleSubmit}>
        {CRITERIA.map((c) => (
          <ScoreSlider key={c.key} label={c.label} value={Number(scores[c.key])} onChange={(v) => set(c.key, v)} />
        ))}
        <div className="ac-form-group" style={{ marginTop: "1rem" }}>
          <label>Assessed By *</label>
          <select required value={scores.assessedBySpeakerId} onChange={(e) => set("assessedBySpeakerId", e.target.value)}>
            <option value="">— Select instructor —</option>
            {speakers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
        <div className="ac-form-group">
          <label>Instructor Feedback</label>
          <textarea rows={3} value={scores.instructorFeedback}
            onChange={(e) => set("instructorFeedback", e.target.value)}
            placeholder="Overall feedback for the student…" />
        </div>
        <div className="ac-form-group">
          <label>Improvement Goals</label>
          <textarea rows={2} value={scores.improvementGoals}
            onChange={(e) => set("improvementGoals", e.target.value)}
            placeholder="What should the student focus on next…" />
        </div>
        <div className="ac-form-actions">
          <button type="submit" className="ac-btn ac-btn-success" disabled={saving}>
            {saving ? "Saving…" : "Save Assessment"}
          </button>
          <button type="button" className="ac-btn ac-btn-ghost" onClick={onCancel}>Cancel</button>
        </div>
      </form>
    </div>
  );
};

/* ─────────────────────────────────────────────
   Main page
───────────────────────────────────────────── */
const AdminAssessmentPage = () => {
  const { enrollmentId } = useParams();
  const navigate         = useNavigate();
  const { state }        = useLocation();
  const courseId         = state?.courseId;

  const [courseAssess, setCourseAssess]   = useState({ initial: null, final: null });
  const [curriculum, setCurriculum]       = useState([]);
  const [speakers, setSpeakers]           = useState([]);
  const [loading, setLoading]             = useState(true);
  const [saving, setSaving]               = useState(false);
  const [error, setError]                 = useState("");
  const [success, setSuccess]             = useState("");
  const [openForm, setOpenForm]           = useState(null); // { scope, type, curriculumId, lessonLabel, existingData }
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [lessonAssess, setLessonAssess]   = useState({ initial: null, final: null });
  const [lessonLoading, setLessonLoading] = useState(false);

  const loadCourseAssess = useCallback(async () => {
    const res = await fetchCourseAssessments(enrollmentId);
    const arr = res.data || [];
    setCourseAssess({
      initial: arr.find((a) => a.assessmentType === "INITIAL") || null,
      final:   arr.find((a) => a.assessmentType === "FINAL")   || null,
    });
  }, [enrollmentId]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const promises = [loadCourseAssess(), fetchActiveSpeakers()];
      if (courseId) promises.push(fetchCurriculum(courseId));
      const [, spkRes, currRes] = await Promise.all(promises);
      setSpeakers(spkRes.data || []);
      setCurriculum(currRes ? (currRes.data || []).sort((a, b) => a.sortOrder - b.sortOrder) : []);
    } catch {
      setError("Failed to load assessments.");
    } finally {
      setLoading(false);
    }
  }, [loadCourseAssess, courseId]);

  useEffect(() => { load(); }, [load]);

  const loadLessonAssess = async (lesson) => {
    setSelectedLesson(lesson);
    setLessonLoading(true);
    setLessonAssess({ initial: null, final: null });
    try {
      const res = await fetchLessonAssessments(enrollmentId, lesson.id);
      const arr = res.data || [];
      setLessonAssess({
        initial: arr.find((a) => a.assessmentType === "INITIAL") || null,
        final:   arr.find((a) => a.assessmentType === "FINAL")   || null,
      });
    } catch {
      setLessonAssess({ initial: null, final: null });
    } finally {
      setLessonLoading(false);
    }
  };

  const handleSave = async (formData) => {
    if (!formData.assessedBySpeakerId) { setError("Please select the assessing instructor."); return; }
    setSaving(true); setError(""); setSuccess("");
    try {
      const base = {
        assessmentType:      formData.assessmentType,
        scoreFluency:        Number(formData.scoreFluency),
        scoreClarity:        Number(formData.scoreClarity),
        scoreVocabulary:     Number(formData.scoreVocabulary),
        scoreGrammar:        Number(formData.scoreGrammar),
        scoreInteraction:    Number(formData.scoreInteraction),
        instructorFeedback:  formData.instructorFeedback,
        improvementGoals:    formData.improvementGoals,
        assessedBySpeakerId: Number(formData.assessedBySpeakerId),
      };
      if (formData.scope === "COURSE") {
        await submitCourseAssessment(enrollmentId, base);
        await loadCourseAssess();
      } else {
        await submitLessonAssessment(enrollmentId, { ...base, curriculumId: formData.curriculumId });
        if (selectedLesson) await loadLessonAssess(selectedLesson);
      }
      setSuccess("Assessment saved successfully.");
      setOpenForm(null);
    } catch (err) {
      setError(String(err?.response?.data?.message || err?.response?.data || "Save failed."));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="ac-loading">Loading assessments…</div>;

  const { initial: cInitial, final: cFinal } = courseAssess;
  const bothExist = cInitial && cFinal;
  const delta     = bothExist ? cFinal.totalScore - cInitial.totalScore : null;

  return (
    <div>
      {/* header */}
      <div className="ac-header">
        <div>
          <h2>Assessments</h2>
          <div style={{ fontSize: "0.85rem", color: "#6b7280", marginTop: "0.2rem" }}>Enrollment #{enrollmentId}</div>
        </div>
        <button className="ac-btn ac-btn-ghost" onClick={() => navigate(-1)}>← Back</button>
      </div>

      {error   && <div style={{ background: "#fee2e2", color: "#b91c1c", padding: "0.6rem 0.9rem", borderRadius: 6, marginBottom: "1rem", fontSize: "0.88rem" }}>{error}</div>}
      {success && <div style={{ background: "#dcfce7", color: "#15803d", padding: "0.6rem 0.9rem", borderRadius: 6, marginBottom: "1rem", fontSize: "0.88rem" }}>{success}</div>}

      {/* ══════════════════════════════════════
          SECTION 1 — COURSE ASSESSMENT
          (no lesson, no curriculumId)
      ══════════════════════════════════════ */}
      <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: "1.25rem", marginBottom: "1.5rem", boxShadow: "0 1px 4px rgba(0,0,0,0.05)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
          <div>
            <h3 style={{ margin: 0, fontSize: "1rem", fontWeight: 700, color: "#0f172a" }}>📊 Course Assessment</h3>
            <p style={{ margin: "3px 0 0", fontSize: "0.78rem", color: "#6b7280" }}>Overall performance for the entire course</p>
          </div>
          {openForm?.scope !== "COURSE" && (
            <div style={{ display: "flex", gap: "0.5rem" }}>
              <button className="ac-btn ac-btn-primary ac-btn-sm"
                onClick={() => { setOpenForm({ scope: "COURSE", type: "INITIAL", curriculumId: null, lessonLabel: null, existingData: cInitial }); setError(""); setSuccess(""); }}>
                {cInitial ? "Edit Initial" : "+ Initial"}
              </button>
              <button className="ac-btn ac-btn-success ac-btn-sm"
                onClick={() => { setOpenForm({ scope: "COURSE", type: "FINAL", curriculumId: null, lessonLabel: null, existingData: cFinal }); setError(""); setSuccess(""); }}>
                {cFinal ? "Edit Final" : "+ Final"}
              </button>
            </div>
          )}
        </div>

        {bothExist && (
          <div style={{ background: delta >= 0 ? "#dcfce7" : "#fee2e2", border: `1px solid ${delta >= 0 ? "#86efac" : "#fca5a5"}`, borderRadius: 8, padding: "0.75rem 1rem", display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "1rem" }}>
            <span style={{ fontSize: "1.4rem" }}>{delta >= 0 ? "🎉" : "📉"}</span>
            <div>
              <div style={{ fontWeight: 700, color: delta >= 0 ? "#15803d" : "#b91c1c", fontSize: "0.95rem" }}>
                {delta >= 0 ? `+${delta}` : delta} points {delta >= 0 ? "improvement" : "change"}
              </div>
              <div style={{ fontSize: "0.8rem", color: "#6b7280" }}>{cInitial.totalScore}/25 → {cFinal.totalScore}/25</div>
            </div>
          </div>
        )}

        {openForm?.scope === "COURSE" ? (
          <AssessForm
            type={openForm.type} scope="COURSE" curriculumId={null} lessonLabel={null}
            existingData={openForm.existingData} speakers={speakers}
            onSave={handleSave} onCancel={() => { setOpenForm(null); setError(""); }}
            saving={saving} error={error}
          />
        ) : (
          <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
            <AssessCard data={cInitial} type="INITIAL" compareData={null}
              onEdit={() => { setOpenForm({ scope: "COURSE", type: "INITIAL", curriculumId: null, lessonLabel: null, existingData: cInitial }); setError(""); setSuccess(""); }} />
            <AssessCard data={cFinal} type="FINAL" compareData={cInitial}
              onEdit={() => { setOpenForm({ scope: "COURSE", type: "FINAL", curriculumId: null, lessonLabel: null, existingData: cFinal }); setError(""); setSuccess(""); }} />
          </div>
        )}
      </div>

      {/* ══════════════════════════════════════
          SECTION 2 — LESSON ASSESSMENTS
          (per lesson, uses curriculumId)
      ══════════════════════════════════════ */}
      {curriculum.length > 0 && (
        <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: "1.25rem", boxShadow: "0 1px 4px rgba(0,0,0,0.05)" }}>
          <div style={{ marginBottom: "1rem" }}>
            <h3 style={{ margin: 0, fontSize: "1rem", fontWeight: 700, color: "#0f172a" }}>📝 Lesson Assessments</h3>
            <p style={{ margin: "3px 0 0", fontSize: "0.78rem", color: "#6b7280" }}>Per-lesson performance — independent of course assessment</p>
          </div>

          {/* lesson picker */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem", marginBottom: "1.25rem" }}>
            {curriculum.map((lesson) => (
              <button
                key={lesson.id}
                className={`ac-btn ac-btn-sm ${selectedLesson?.id === lesson.id ? "ac-btn-primary" : "ac-btn-ghost"}`}
                onClick={() => { setOpenForm(null); loadLessonAssess(lesson); }}
              >
                D{lesson.dayNumber}
                {lesson.isAssessment && (
                  <span style={{ marginLeft: 3, color: selectedLesson?.id === lesson.id ? "#fff" : "#f59e0b" }}>★</span>
                )}
              </button>
            ))}
          </div>

          {!selectedLesson && (
            <div style={{ color: "#9ca3af", fontSize: "0.88rem", padding: "0.5rem 0" }}>
              Select a lesson above to view or add its assessment.
            </div>
          )}

          {selectedLesson && lessonLoading && (
            <div className="ac-loading" style={{ padding: "1rem" }}>Loading…</div>
          )}

          {selectedLesson && !lessonLoading && (
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
                <div style={{ fontWeight: 700, fontSize: "0.95rem", color: "#374151" }}>
                  Day {selectedLesson.dayNumber}: {selectedLesson.title}
                </div>
                {openForm?.scope !== "LESSON" && (
                  <div style={{ display: "flex", gap: "0.5rem" }}>
                    <button className="ac-btn ac-btn-primary ac-btn-sm"
                      onClick={() => { setOpenForm({ scope: "LESSON", type: "INITIAL", curriculumId: selectedLesson.id, lessonLabel: `Day ${selectedLesson.dayNumber}: ${selectedLesson.title}`, existingData: lessonAssess.initial }); setError(""); setSuccess(""); }}>
                      {lessonAssess.initial ? "Edit Initial" : "+ Initial"}
                    </button>
                    <button className="ac-btn ac-btn-success ac-btn-sm"
                      onClick={() => { setOpenForm({ scope: "LESSON", type: "FINAL", curriculumId: selectedLesson.id, lessonLabel: `Day ${selectedLesson.dayNumber}: ${selectedLesson.title}`, existingData: lessonAssess.final }); setError(""); setSuccess(""); }}>
                      {lessonAssess.final ? "Edit Final" : "+ Final"}
                    </button>
                  </div>
                )}
              </div>

              {openForm?.scope === "LESSON" ? (
                <AssessForm
                  type={openForm.type} scope="LESSON"
                  curriculumId={openForm.curriculumId} lessonLabel={openForm.lessonLabel}
                  existingData={openForm.existingData} speakers={speakers}
                  onSave={handleSave} onCancel={() => { setOpenForm(null); setError(""); }}
                  saving={saving} error={error}
                />
              ) : (
                <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
                  <AssessCard data={lessonAssess.initial} type="INITIAL" compareData={null}
                    onEdit={() => { setOpenForm({ scope: "LESSON", type: "INITIAL", curriculumId: selectedLesson.id, lessonLabel: `Day ${selectedLesson.dayNumber}: ${selectedLesson.title}`, existingData: lessonAssess.initial }); setError(""); setSuccess(""); }} />
                  <AssessCard data={lessonAssess.final} type="FINAL" compareData={lessonAssess.initial}
                    onEdit={() => { setOpenForm({ scope: "LESSON", type: "FINAL", curriculumId: selectedLesson.id, lessonLabel: `Day ${selectedLesson.dayNumber}: ${selectedLesson.title}`, existingData: lessonAssess.final }); setError(""); setSuccess(""); }} />
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminAssessmentPage;
